import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";
import { JourneyDimensionSchema, type EnvironmentDetailResponse, type JourneyDimension, type JourneyEdge } from "@starsach/shared-types";
import { useContactSearch, useJourneyPaths } from "../../api/hooks";
import { Sankey } from "../../components/Sankey";
import { toSteps, type StepEdge } from "../../lib/journey-steps";
import { branchText, edgeTitle, readableLabel } from "../../lib/journeys";
import { SetupChecklist } from "../../components/SetupChecklist";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, inputClass } from "../../components/ui";
import { pathsChecks, pathsSliceOnly } from "../../lib/setup-checks";
import { useFormat } from "../../lib/useFormat";

const DIMENSION_LABEL: Record<JourneyDimension, string> = { all: "All contacts", channel: "Channel", queue: "Queue", dnis: "Number dialed", flow: "Contact flow" };

/** design.md "Journeys module": View 1 (Sankey over pushed rollups); an empty one says why (#348). */
export function PathsView({ environmentId, canInspect, detail }: { environmentId: string; canInspect: boolean; detail: EnvironmentDetailResponse }) {
  const [dimension, setDimension] = useState<JourneyDimension>("all");
  const [slice, setSlice] = useState("*");
  const [days, setDays] = useState(7);
  const [selected, setSelected] = useState<JourneyEdge | null>(null);
  const [showBlocks, setShowBlocks] = useState(false);
  const q = useJourneyPaths(environmentId, dimension, slice, days);
  // ADR-0012 phase 1: steps by default - plumbing contracted, retries folded, branches named by destination - blocks on request.
  const view = useMemo(() => {
    if (!q.data) return undefined;
    if (showBlocks) return { edges: q.data.edges as StepEdge[], retries: undefined, collapsed: 0 };
    const g = toSteps(q.data.edges);
    return { edges: g.edges, retries: g.retries, collapsed: g.collapsed };
  }, [q.data, showBlocks]);

  return (
    <Card title="Contact flow paths">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs text-neutral-500">Slice by</span>
          <select className={inputClass} value={dimension} onChange={(e) => { const d = JourneyDimensionSchema.parse(e.target.value); setDimension(d); setSlice(d === "all" ? "*" : (q.data?.availableSlices[d]?.[0] ?? "*")); setSelected(null); }}>
            {JourneyDimensionSchema.options.map((d) => <option key={d} value={d}>{DIMENSION_LABEL[d]}</option>)}
          </select>
        </label>
        {dimension !== "all" && (
          <label className="text-sm">
            <span className="block text-xs text-neutral-500">Value</span>
            <select className={inputClass} value={slice} onChange={(e) => { setSlice(e.target.value); setSelected(null); }}>
              {(q.data?.availableSlices[dimension] ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
              {(q.data?.availableSlices[dimension] ?? []).length === 0 && <option value={slice}>{dimension === "dnis" ? "not available in v1" : "none yet"}</option>}
            </select>
          </label>
        )}
        <label className="text-sm">
          <span className="block text-xs text-neutral-500">Window</span>
          <select className={inputClass} value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>1 day</option><option value={7}>7 days</option><option value={30}>30 days</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showBlocks} onChange={(e) => { setShowBlocks(e.target.checked); setSelected(null); }} />
          Show every block
        </label>
        {q.data && view && (
          <p className="ml-auto text-sm text-neutral-600 dark:text-neutral-400">
            {q.data.contactCount.toLocaleString()} contacts · {view.edges.length} transitions{view.collapsed ? ` · ${view.collapsed} plumbing block${view.collapsed === 1 ? "" : "s"} folded in` : ""}
          </p>
        )}
      </div>
      {q.isPending && <LoadingState label="Loading paths" />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && q.data.edges.length === 0 &&
        (pathsSliceOnly(q.data) ? (
          <EmptyState title="No paths in this slice" hint={`This window's rollups counted ${q.data.rollups.contactsInWindow.toLocaleString()} contacts, none of them in this slice. Pick another value or widen the window.`} />
        ) : (
          <SetupChecklist title="No paths in this window" checks={pathsChecks(detail, q.data)} />
        ))}
      {q.data && view && q.data.edges.length > 0 && (
        <>
          <Sankey edges={view.edges} retries={view.retries} onSelectEdge={setSelected} selected={selected} />
          {!showBlocks && view.collapsed > 0 && (
            <p className="mt-1 text-xs text-neutral-500">
              Steps a caller acts at, with the blocks in between folded in; counts through a folded block are apportioned, so they are close rather than exact. A retry loop is the number on the step it returns to.
            </p>
          )}
          {selected && (
            <div className="mt-4 rounded border border-neutral-200 p-3 dark:border-neutral-800" aria-live="polite">
              <p className="text-sm font-medium">{edgeTitle(selected)}: {selected.count} contacts{selected.errorCount ? `, ${selected.errorCount} errors` : ""}{selected.avgDwellMs ? `, avg ${(selected.avgDwellMs / 1000).toFixed(1)}s in ${readableLabel(selected.fromLabel, selected.from)}` : ""}</p>
              {selected.sampleContactIds && selected.sampleContactIds.length > 0 ? (
                <ul className="mt-2 flex flex-wrap gap-2 text-sm">
                  {selected.sampleContactIds.map((id) => (
                    <li key={id}>
                      {canInspect ? <Link className="font-mono text-xs text-accent-strong underline" to={`/journeys/${environmentId}/contacts/${id}`}>{id}</Link> : <span className="font-mono text-xs">{id}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-neutral-500">No sample contacts recorded for this transition.</p>
              )}
              <p className="mt-2 text-xs text-neutral-500">Recent contacts on this transition (up to 20). {canInspect ? "Open one for the full call." : "Single-call inspection is available to Gairm staff."}</p>
            </div>
          )}
          <DataTable
            caption="Transitions in this slice"
            rows={view.edges}
            rowKey={(e) => `${e.from}->${e.to}#${e.branch ?? ""}`}
            empty={null}
            columns={[
              { key: "from", header: "From", render: (e) => readableLabel(e.fromLabel, e.from) },
              {
                key: "branch",
                header: "Branch",
                render: (e) => (
                  <>
                    {e.meaning ? <><span className="font-medium">{e.meaning}</span> <span className="text-neutral-500">({branchText(e)})</span></> : (branchText(e) ?? "")}
                    {e.via && <span className="block text-xs text-neutral-500" title="What the caller heard on the way">heard: {e.via}</span>}
                  </>
                ),
              },
              { key: "to", header: "To", render: (e) => readableLabel(e.toLabel, e.to) },
              { key: "count", header: "Contacts", numeric: true, render: (e) => e.count },
              { key: "dwell", header: "Avg time in block", numeric: true, render: (e) => (e.avgDwellMs === undefined ? "-" : `${(e.avgDwellMs / 1000).toFixed(1)}s`) },
              { key: "errors", header: "Errors", numeric: true, render: (e) => (e.errorCount ? <span className="font-semibold text-red-700 dark:text-red-300">{e.errorCount}</span> : 0) },
            ]}
          />
        </>
      )}
    </Card>
  );
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const LookupSchema = z
  .object({
    from: z.string().regex(DAY, "Pick a start day"),
    to: z.string().regex(DAY, "Pick an end day"),
    ani: z.string().max(32),
    dnis: z.string().max(32),
  })
  .refine((v) => v.ani.replace(/\D/g, "").length >= 4 || v.dnis.replace(/\D/g, "").length >= 4, { message: "Enter at least one number (last four digits are enough)", path: ["ani"] })
  .refine((v) => v.to >= v.from, { message: "The end day is before the start", path: ["to"] })
  .refine((v) => (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000 <= 90, { message: "At most 90 days", path: ["to"] });

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** "from a number ending 0123", "to a number ending 0100", or both - what the empty state says was searched. */
function searched(p: { ani: string; dnis: string } | null): string {
  const last = (s: string) => s.replace(/\D/g, "").slice(-4);
  const parts = [p?.ani && last(p.ani) ? `from a number ending ${last(p.ani)}` : "", p?.dnis && last(p.dnis) ? `to a number ending ${last(p.dnis)}` : ""].filter(Boolean);
  return parts.join(" ");
}

/** View 2's direct entry: the complaint case, where all anyone knows is the day and the numbers. */
export function CallLookup({ environmentId }: { environmentId: string }) {
  const fmt = useFormat();
  const [params, setParams] = useState<{ from: string; to: string; ani: string; dnis: string } | null>(null);
  // "Sometime last week" is the usual starting point (ADR-0016), so the default is the last thirty days, not today.
  const form = useForm<z.infer<typeof LookupSchema>>({ resolver: zodResolver(LookupSchema), defaultValues: { from: isoDay(new Date(Date.now() - 30 * 86_400_000)), to: isoDay(new Date()), ani: "", dnis: "" } });
  const search = useContactSearch(environmentId, params);
  const errs = form.formState.errors;
  return (
    <Card title="Find a call">
      <form className="flex flex-wrap items-end gap-3" onSubmit={form.handleSubmit((v) => setParams(v))}>
        <div>
          <label htmlFor="lookup-from" className="block text-sm font-medium">From</label>
          <input id="lookup-from" type="date" {...form.register("from")} aria-invalid={Boolean(errs.from)} aria-describedby={errs.from ? "lookup-from-error" : undefined} className={inputClass} />
          {errs.from && <p id="lookup-from-error" role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{errs.from.message}</p>}
        </div>
        <div>
          <label htmlFor="lookup-to" className="block text-sm font-medium">To</label>
          <input id="lookup-to" type="date" {...form.register("to")} aria-invalid={Boolean(errs.to)} aria-describedby={errs.to ? "lookup-to-error" : undefined} className={inputClass} />
          {errs.to && <p id="lookup-to-error" role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{errs.to.message}</p>}
        </div>
        <div>
          <label htmlFor="lookup-ani" className="block text-sm font-medium">Calling number</label>
          <input id="lookup-ani" type="tel" placeholder="+1 415 555 0123" {...form.register("ani")} aria-invalid={Boolean(errs.ani)} aria-describedby={errs.ani ? "lookup-ani-error" : undefined} className={inputClass} />
          {errs.ani && <p id="lookup-ani-error" role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{errs.ani.message}</p>}
        </div>
        <div>
          <label htmlFor="lookup-dnis" className="block text-sm font-medium">Number dialed</label>
          <input id="lookup-dnis" type="tel" placeholder="+1 800 555 0100" {...form.register("dnis")} className={inputClass} />
        </div>
        <button type="submit" className="min-h-9 rounded bg-accent px-4 text-sm font-medium text-white hover:bg-accent-strong">Search</button>
      </form>
      <p className="mt-2 text-xs text-neutral-600 dark:text-neutral-400">Searches the customer's Connect instance through their opt-in role. Every search is audit-logged.</p>
      {search.isFetching && <LoadingState label="Searching the day's calls" />}
      {search.isError && <ErrorState error={search.error} onRetry={() => void search.refetch()} />}
      {search.data && (
        <div className="mt-4">
          {search.data.truncated && <p className="mb-2 text-sm text-amber-700 dark:text-amber-300">Busy day: only the first 300 calls were checked. Narrow the numbers.</p>}
          <DataTable
            caption="Matching contacts"
            rows={search.data.candidates}
            rowKey={(c) => c.contactId}
            empty={<EmptyState title="No calls matched" hint={`No voice calls ${searched(params)} between ${fmt.date(`${params!.from}T12:00:00Z`)} and ${fmt.date(`${params!.to}T12:00:00Z`)} (days in UTC). Widen the range, or try the last four digits only.`} />}
            columns={[
              { key: "when", header: "Started", render: (c) => <span title={c.initiationTimestamp}>{fmt.time(c.initiationTimestamp)} · <RelativeTime iso={c.initiationTimestamp} /></span> },
              { key: "dur", header: "Duration", numeric: true, render: (c) => (c.disconnectTimestamp ? `${Math.round((Date.parse(c.disconnectTimestamp) - Date.parse(c.initiationTimestamp)) / 1000)}s` : "-") },
              { key: "queue", header: "Queue", render: (c) => c.queueName ?? "-" },
              { key: "agent", header: "Agent", render: (c) => <StatusPill status={c.agentConnected ? "answered" : "no agent"} tone={c.agentConnected ? "ok" : "warning"} /> },
              { key: "open", header: "", render: (c) => <Link className="text-accent-strong underline" to={`/journeys/${environmentId}/contacts/${c.contactId}`}>Open call</Link> },
            ]}
          />
        </div>
      )}
    </Card>
  );
}
